import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import { PermissionsByRoleQuery, usePermissionsByRoleLazyQuery } from '@/__generated__/graphql';
import { useAtom } from 'jotai';
import { userManagementSearchTerm } from '../state';
import { useDebounce } from '@/hooks/useDebounce';
import { Order } from '@/types';
import { TableSkeleton } from '../shared/TableSkeleton';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { TablePagination } from '../../shared/TablePagination';
import { defaultPaginationSize } from '@/constants';

type Props = { selectedRole?: string };

type Permission = PermissionsByRoleQuery['permissionsByRole'][0];
type Headers = keyof Permission;
type Column = { id: Headers; numeric: boolean; disablePadding: boolean; label: string };

const columns: Column[] = [
  { id: 'key', label: 'Permission', numeric: false, disablePadding: true },
  { id: 'description', label: 'Description', numeric: false, disablePadding: false },
];

export const PermissionsTable = ({ selectedRole }: Props) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<Headers>('key');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [allRolePermissions, setAllRolePermissions] = useState<Permission[]>([]);

  const [searchValue, setSearchTerm] = useAtom(userManagementSearchTerm);
  const searchTerm = useDebounce(searchValue, 700);

  useEffect(() => {
    return () => {
      setSearchTerm('');
    };
  }, [setSearchTerm]);

  const [getPermissions, { data, loading }] = usePermissionsByRoleLazyQuery();

  useEffect(() => {
    if (selectedRole) getPermissions({ variables: { roleId: selectedRole } });
  }, [selectedRole, getPermissions]);

  useEffect(() => {
    setAllRolePermissions(data?.permissionsByRole ?? []);
  }, [data?.permissionsByRole]);

  const rolePermissions = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase();
    const normalize = (text: string) => text.replace(/[:_]/g, ' ').toLowerCase();

    const filteredPermissions = allRolePermissions.filter(({ key, description }) => {
      return [key, description].some((text) => normalize(text).includes(searchTermLower));
    });

    const sortedPermissions = filteredPermissions.sort((a, b) => {
      const aValue = normalize(a[orderBy] ?? '');
      const bValue = normalize(b[orderBy] ?? '');

      return aValue.localeCompare(bValue) * (order === 'asc' ? 1 : -1);
    });

    const start = page * rowsPerPage;
    return sortedPermissions.slice(start, start + rowsPerPage);
  }, [allRolePermissions, order, orderBy, page, rowsPerPage, searchTerm]);

  const handleRequestSort = (headerId: Headers) => {
    const isAsc = orderBy === headerId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerId);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <>
      <TableContainer sx={{ overflowY: 'auto', mt: 3 }}>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          {!loading && rolePermissions.length > 0 && (
            <TableHead>
              <TableRow>
                {columns.map(({ id, label }) => (
                  <TableCell
                    key={id}
                    align="left"
                    padding="normal"
                    sortDirection={orderBy === id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === id}
                      direction={orderBy === id ? order : 'asc'}
                      onClick={() => handleRequestSort(id)}
                    >
                      <Typography
                        fontSize="1.2rem"
                        sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase' }}
                      >
                        {label}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {loading && <TableSkeleton showActionColumn={false} showAvatarColumn={false} />}
            {!loading &&
              rolePermissions.map((row, index) => (
                <TableRow hover key={row.key}>
                  <StyledTableCell id={`${row.key}-${index}`} sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{row.key}</Box>
                  </StyledTableCell>
                  <StyledTableCell>{row.description ?? 'N/A'}</StyledTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && rolePermissions.length === 0 && (
        <TableDataNotFound message="No Permissions Found" />
      )}

      {!loading && rolePermissions.length > 0 && (
        <TablePaginationContainer>
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            count={allRolePermissions.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[
              ...defaultPaginationSize,
              { label: 'All', value: allRolePermissions.length },
            ]}
          />
        </TablePaginationContainer>
      )}
    </>
  );
};
